import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SprayCan, Plus, Trash2, Download, Printer, ChevronLeft, ChevronRight, Loader2, AlertTriangle, X, ShieldCheck, ShieldAlert, ExternalLink } from 'lucide-react';
import { pesticide, apiCustomers, pesticideProducts } from '../../lib/api';
import { toCSV, downloadFile, printElement } from '../../lib/importExport';
import { toast } from 'sonner';
import { shiftDays, shiftMonths, thisMonth, todayISO } from '../../lib/dates';

const UNITS = ['fl oz', 'oz', 'gal', 'qt', 'pt', 'mL', 'L', 'lb', 'g', 'kg', 'each'];
const SITES = ['Structural', 'Landscape', 'Right-of-way', 'Vector', 'Food/Feed', 'Regulatory', 'Other'];
const num = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 3 });

const blankForm = () => ({
  date: todayISO(), customerId: '', customerName: '', address: '',
  productId: '', productName: '', epaRegNo: '', activeIngredient: '', concentration: '',
  amount: '', unit: 'fl oz', amountUndiluted: '', amountDiluted: '',
  targetPest: '', site: '', method: '', equipment: '', dilutionRate: '',
  weather: '', tempF: '', applicatorName: '', applicatorLicense: '', notes: '',
});

export default function PesticideLogPage() {
  const [customers, setCustomers] = useState([]);
  const [period, setPeriod] = useState('week');   // 'week' | 'month'
  const [weekDate, setWeekDate] = useState(todayISO());
  const [month, setMonth] = useState(thisMonth());
  const [report, setReport] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [vocab, setVocab] = useState({ site: [], method: [], equipment: [], dilution: [], unit: [] });

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = period === 'month' ? { period: 'month', month } : { period: 'week', date: weekDate };
      const r = await pesticide.report(params);
      setReport(r);
      const list = await pesticide.list(r.from, r.to);
      setApps(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error('Could not load the report');
    } finally { setLoading(false); }
  }, [period, weekDate, month]);

  useEffect(() => { apiCustomers.list().then((c) => setCustomers(Array.isArray(c) ? c : [])).catch(() => {}); }, []);
  useEffect(() => { pesticideProducts.list().then((p) => setProducts(Array.isArray(p) ? p : [])).catch(() => {}); }, []);
  useEffect(() => { pesticideProducts.vocab().then(setVocab).catch(() => {}); }, []);
  useEffect(() => { loadReport(); }, [loadReport]);

  const onCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({ ...f, customerId: id, customerName: c ? (c.name || c.customerName || '') : '', address: c ? (c.address || c.serviceAddress || '') : '' }));
  };

  // Picking a catalogued product fills the regulated fields, so a technician
  // never retypes an EPA number. "Other" keeps the free-text path open.
  const onProduct = (id) => {
    if (id === '__other__') {
      setForm((f) => ({ ...f, productId: '__other__', productName: '', epaRegNo: '', activeIngredient: '', concentration: '' }));
      return;
    }
    const p = products.find((x) => x.id === id);
    if (!p) { setForm((f) => ({ ...f, productId: '' })); return; }
    setForm((f) => ({
      ...f, productId: p.id, productName: p.name, epaRegNo: p.epaRegNo || '',
      activeIngredient: p.activeIngredient || '', concentration: p.concentration || '',
    }));
  };

  const chosen = products.find((x) => x.id === form.productId);


  const save = async () => {
    if (!form.productName) { toast.error('Enter the product name'); return; }
    if (!form.amount) { toast.error('Enter the amount used'); return; }
    setSaving(true);
    try {
      await pesticide.create({
        ...form,
        productId: form.productId === '__other__' ? null : (form.productId || null),
        amount: Number(form.amount) || 0,
        amountUndiluted: form.amountUndiluted === '' ? null : Number(form.amountUndiluted),
        amountDiluted: form.amountDiluted === '' ? null : Number(form.amountDiluted),
        tempF: form.tempF === '' ? null : Number(form.tempF),
      });
      toast.success('Application logged');
      setForm(blankForm()); setShowForm(false); loadReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not log application');
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm('Delete this application record?')) return;
    try { await pesticide.remove(a.id); toast.success('Deleted'); loadReport(); }
    catch { toast.error('Could not delete'); }
  };

  const exportCSV = () => {
    if (!report || !report.lineItems.length) { toast.error('Nothing to export'); return; }
    const cols = [
      { key: 'productName', header: 'Product' }, { key: 'epaRegNo', header: 'EPA Reg No' },
      { key: 'activeIngredient', header: 'Active Ingredient' }, { key: 'totalAmount', header: 'Amount Used' },
      { key: 'unit', header: 'Unit' }, { key: 'applications', header: 'Applications' },
      { key: 'lbsActiveIngredient', header: 'Lbs Active Ingredient' },
    ];
    downloadFile(`pesticide-use-${report.from}-to-${report.to}.csv`, toCSV(report.lineItems, cols));
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <SprayCan className="w-6 h-6 text-[#8BC53F]" />
          <h1 className="text-2xl font-extrabold text-slate-900">Pesticide Use Log</h1>
        </div>
        <Button onClick={() => setShowForm((s) => !s)} className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold gap-1.5" data-testid="log-app-btn">
          <Plus className="w-4 h-4" /> Log Application
        </Button>
      </div>
      <p className="text-slate-500 text-sm mt-1">Log each application and get the amount used automatically — by week, or as the California DPR monthly summary.</p>

      {showForm && (
        <Card className="mt-4 p-5 border-slate-200" data-testid="app-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-900">Log an application</h2>
            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-slate-600">Date</label><Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="mt-1 h-10" /></div>
            <div>
              <label className="text-xs font-medium text-slate-600">Customer</label>
              <select value={form.customerId} onChange={(e) => onCustomer(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm">
                <option value="">— optional —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.customerName || c.id}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Applicator license / QAL #</label><Input value={form.applicatorLicense} onChange={(e) => setForm((f) => ({ ...f, applicatorLicense: e.target.value }))} placeholder="QAL 12345" className="mt-1 h-10" /></div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className="text-xs font-medium text-slate-600">Product *</label>
              <select value={form.productId} onChange={(e) => onProduct(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm" data-testid="product-picker">
                <option value="">— choose a product —</option>
                {Object.entries(products.reduce((acc, p) => { const k = p.category || 'Other'; if (!acc[k]) acc[k] = []; acc[k].push(p); return acc; }, {})).map(([cat, list]) => (
                  <optgroup key={cat} label={cat}>
                    {list.map((p) => <option key={p.id} value={p.id}>{p.name}{p.epaRegNo ? ` · EPA ${p.epaRegNo}` : ''}</option>)}
                  </optgroup>
                ))}
                <option value="__other__">Other / not in catalog…</option>
              </select>
            </div>

            {chosen && (
              <div className="sm:col-span-2 lg:col-span-3">
                {chosen.verified ? (
                  <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="product-verified">
                    <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>EPA Reg # and active ingredient verified against the label. Still confirm the container matches before applying.</span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="product-verify-warning">
                    <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <b>Verify before recording.</b> This product's EPA Reg # has not been confirmed for this build. Check the number on the container and its CDPR Active status.{' '}
                      <a href="https://apps.cdpr.ca.gov/docs/label/labelque.cfm" target="_blank" rel="noreferrer" className="underline font-semibold inline-flex items-center gap-0.5">CDPR label lookup <ExternalLink className="w-3 h-3" /></a>
                    </span>
                  </div>
                )}
                {chosen.restriction && (
                  <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="product-restriction">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><b>Restriction:</b> {chosen.restriction}</span>
                  </div>
                )}
              </div>
            )}

            <div><label className="text-xs font-medium text-slate-600">Product name *</label><Input value={form.productName} onChange={(e) => setForm((f) => ({ ...f, productName: e.target.value }))} placeholder="Tirade Ultra SC" className="mt-1 h-10" readOnly={!!chosen} /></div>
            <div><label className="text-xs font-medium text-slate-600">EPA Reg No.</label><Input value={form.epaRegNo} onChange={(e) => setForm((f) => ({ ...f, epaRegNo: e.target.value }))} placeholder="91234-294" className="mt-1 h-10" /></div>
            <div><label className="text-xs font-medium text-slate-600">Active ingredient</label><Input value={form.activeIngredient} onChange={(e) => setForm((f) => ({ ...f, activeIngredient: e.target.value }))} placeholder="Beta-cyfluthrin" className="mt-1 h-10" /></div>
            <div><label className="text-xs font-medium text-slate-600">Concentration</label><Input value={form.concentration} onChange={(e) => setForm((f) => ({ ...f, concentration: e.target.value }))} placeholder="11.8%" className="mt-1 h-10" /></div>

            <div><label className="text-xs font-medium text-slate-600">Target pest(s)</label><Input value={form.targetPest} onChange={(e) => setForm((f) => ({ ...f, targetPest: e.target.value }))} placeholder="German cockroach" className="mt-1 h-10" /></div>
            <div><label className="text-xs font-medium text-slate-600">Weather</label><Input value={form.weather} onChange={(e) => setForm((f) => ({ ...f, weather: e.target.value }))} placeholder="Clear, light wind" className="mt-1 h-10" /></div>
            <div><label className="text-xs font-medium text-slate-600">Temp (°F)</label><Input type="number" value={form.tempF} onChange={(e) => setForm((f) => ({ ...f, tempF: e.target.value }))} placeholder="72" className="mt-1 h-10" /></div>

            <div><label className="text-xs font-medium text-slate-600">Site / area treated</label>
              <select value={form.site} onChange={(e) => setForm((f) => ({ ...f, site: e.target.value }))} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm" data-testid="site-select">
                <option value="">— select site —</option>
                {(vocab.site || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Application method</label>
              <select value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm" data-testid="method-select">
                <option value="">— select method —</option>
                {(vocab.method || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Equipment used</label>
              <select value={form.equipment} onChange={(e) => setForm((f) => ({ ...f, equipment: e.target.value }))} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm" data-testid="equipment-select">
                <option value="">— select equipment —</option>
                {(vocab.equipment || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Dilution rate</label>
              <select value={form.dilutionRate} onChange={(e) => setForm((f) => ({ ...f, dilutionRate: e.target.value }))} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm" data-testid="dilution-select">
                <option value="">— select dilution —</option>
                {(vocab.dilution || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div><label className="text-xs font-medium text-slate-600">Amount used *</label><Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="2" className="mt-1 h-10" /></div>
            <div><label className="text-xs font-medium text-slate-600">Unit</label>
              <select value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm">
                {(vocab.unit && vocab.unit.length ? vocab.unit : UNITS).map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Amount undiluted (concentrate)</label><Input type="number" value={form.amountUndiluted} onChange={(e) => setForm((f) => ({ ...f, amountUndiluted: e.target.value }))} placeholder="1.5" className="mt-1 h-10" /></div>
            <div><label className="text-xs font-medium text-slate-600">Amount diluted (total applied)</label><Input type="number" value={form.amountDiluted} onChange={(e) => setForm((f) => ({ ...f, amountDiluted: e.target.value }))} placeholder="128" className="mt-1 h-10" /></div>

            <div><label className="text-xs font-medium text-slate-600">Applicator</label><Input value={form.applicatorName} onChange={(e) => setForm((f) => ({ ...f, applicatorName: e.target.value }))} placeholder="auto: you" className="mt-1 h-10" /></div>
            <div className="sm:col-span-2 lg:col-span-3"><label className="text-xs font-medium text-slate-600">Notes / observations</label><Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1 h-10" /></div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={save} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold">{saving ? 'Saving…' : 'Save application'}</Button>
            <Button onClick={() => setShowForm(false)} variant="outline">Cancel</Button>
          </div>
        </Card>
      )}

      <Card className="mt-4 border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between gap-2 flex-wrap p-4 border-b border-slate-100">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            <button onClick={() => setPeriod('week')} className={`px-4 py-2 text-sm font-semibold ${period === 'week' ? 'bg-[#8BC53F] text-white' : 'bg-white text-slate-600'}`}>This Week</button>
            <button onClick={() => setPeriod('month')} className={`px-4 py-2 text-sm font-semibold ${period === 'month' ? 'bg-[#8BC53F] text-white' : 'bg-white text-slate-600'}`}>Monthly (DPR)</button>
          </div>
          <div className="flex items-center gap-2">
            {period === 'week' ? (
              <>
                <button onClick={() => setWeekDate((d) => shiftDays(d, -7))} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
                <Input type="date" value={weekDate} onChange={(e) => setWeekDate(e.target.value)} className="h-9 w-40" />
                <button onClick={() => setWeekDate((d) => shiftDays(d, 7))} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
              </>
            ) : (
              <>
                <button onClick={() => setMonth((m) => shiftMonths(m, -1))} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" />
                <button onClick={() => setMonth((m) => shiftMonths(m, 1))} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
              </>
            )}
            <button onClick={exportCSV} className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium flex items-center gap-1.5"><Download className="w-4 h-4" />CSV</button>
            <button onClick={() => printElement('#pur-print')} className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium flex items-center gap-1.5"><Printer className="w-4 h-4" />Print</button>
          </div>
        </div>

        <div id="pur-print" className="p-4">
          {loading ? (
            <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : !report ? null : (
            <>
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="text-lg font-extrabold text-slate-900">{report.operator?.name || 'Xpertpests Solutions'}</div>
                  <div className="text-sm text-slate-500">{report.label}{report.operator?.license ? ` · PCO Lic ${report.operator.license}` : ''}</div>
                </div>
                <div className="text-right"><div className="text-xs text-slate-400">Applications</div><div className="text-2xl font-extrabold text-[#6FA82C] tabular-nums">{report.totalApplications}</div></div>
              </div>

              {report.isNegative ? (
                <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-2 text-amber-800" data-testid="negative-report">
                  <AlertTriangle className="w-5 h-5" /> <span className="text-sm font-medium">No applications in this period — file a <b>Negative (No Use)</b> report for the month if required.</span>
                </div>
              ) : (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-3 font-semibold">Product</th>
                        <th className="py-2 pr-3 font-semibold">EPA Reg No.</th>
                        <th className="py-2 pr-3 font-semibold">Active Ingredient</th>
                        <th className="py-2 pr-3 font-semibold text-right">Amount Used</th>
                        <th className="py-2 pr-3 font-semibold text-right">Lbs A.I.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.lineItems.map((r, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="py-2 pr-3 font-medium text-slate-800">{r.productName}</td>
                          <td className="py-2 pr-3 text-slate-600">{r.epaRegNo || '—'}</td>
                          <td className="py-2 pr-3 text-slate-600">{r.activeIngredient || '—'}</td>
                          <td className="py-2 pr-3 text-right font-bold text-slate-900 tabular-nums">{num(r.totalAmount)} {r.unit}</td>
                          <td className="py-2 pr-3 text-right text-slate-600 tabular-nums">{r.lbsActiveIngredient ? num(r.lbsActiveIngredient) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {report.activeIngredients?.length > 0 && (
                    <div className="mt-3 text-xs text-slate-500">
                      <b>Active ingredient totals:</b> {report.activeIngredients.map((a) => `${a.activeIngredient}: ${num(a.lbsActiveIngredient)} lb`).join('  ·  ')}
                    </div>
                  )}
                  {report.unconvertedApplications > 0 && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="ai-incomplete-warning">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div>
                          <b>Active ingredient totals are incomplete.</b>{' '}
                          {report.unconvertedApplications} of {report.totalApplications} application(s) could not be
                          converted to pounds, so they are excluded from the Lbs A.I. figures above. Fix these before
                          filing, or the report will under-state how much active ingredient was applied.
                          <ul className="mt-2 space-y-0.5 list-disc pl-4">
                            {report.unconverted?.slice(0, 8).map((u, i) => (
                              <li key={i}>
                                {u.date} · {u.productName || 'Unnamed product'} ({num(u.amount)} {u.unit}) — {u.reason}
                              </li>
                            ))}
                          </ul>
                          {report.unconvertedApplications > 8 && (
                            <div className="mt-1 italic">…and {report.unconvertedApplications - 8} more.</div>
                          )}
                          <div className="mt-2">
                            Volumetric units need the product’s density (lb/gal) recorded in the product catalog.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {!loading && apps.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-bold text-slate-700 mb-2">Applications this period ({apps.length})</h3>
          <div className="space-y-1.5">
            {apps.map((a) => (
              <Card key={a.id} className="px-4 py-2.5 border-slate-200 flex items-center gap-3 flex-wrap text-sm">
                <span className="text-slate-400 w-24">{a.date}</span>
                <span className="font-medium text-slate-800 flex-1 min-w-[120px]">{a.productName}</span>
                <span className="text-slate-500">{num(a.amount)} {a.unit}</span>
                <span className="text-slate-400 hidden sm:inline">{a.customerName || a.targetPest || ''}</span>
                <button onClick={() => remove(a)} className="text-red-500 hover:bg-red-50 w-7 h-7 rounded-lg flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
